import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { CheckCircle2, Sparkles, ArrowRight, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_INTERVAL_MS = 60 * 1000; // 60s

const SEVERIDAD_BADGE = {
  bajo: "bg-emerald-100 text-emerald-700 border-emerald-300",
  medio: "bg-amber-100 text-amber-700 border-amber-300",
  alto: "bg-orange-100 text-orange-700 border-orange-300",
  critico: "bg-rose-100 text-rose-700 border-rose-300",
};

/**
 * Global popup that appears for ALL authenticated users when a bug they
 * reported (or anyone else) is marked as RESUELTO by an admin. The popup
 * is dismissable per-user (each user dismisses independently) and the
 * announcement is auto-purged after 14 days.
 *
 * Mounted once at the App root, polls /api/reportes-errores/anuncios/activos
 * every minute while the user is logged in.
 */
export default function BugResolvedAnnouncements() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const intervalRef = useRef(null);
  // Bandera: si recibimos 401, paramos el poll hasta el próximo login.
  // Evita inundar el log del servidor con 401s repetidos cada 60s.
  const unauthorizedRef = useRef(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchActive = useCallback(async () => {
    if (!user || !token || unauthorizedRef.current) return;
    try {
      const r = await axios.get(`${API}/reportes-errores/anuncios/activos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = r.data?.items || [];
      if (items.length > 0) {
        setQueue((prev) => {
          // Dedupe vs already queued
          const seen = new Set(prev.map((a) => a.id));
          const merged = [...prev];
          for (const it of items) if (!seen.has(it.id)) merged.push(it);
          return merged;
        });
      }
    } catch (e) {
      // Si el token ya no vale, paramos el poll para no inundar el log
      // del servidor con 401s cada 60s. Se reanuda en el próximo login
      // (el efecto re-monta el intervalo cuando cambian user/token).
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        unauthorizedRef.current = true;
        stopPolling();
      }
      // Otros errores (network blip, 5xx) son ignorados silenciosamente
      // y se reintentan en el próximo tick.
    }
  }, [user, token, stopPolling]);

  useEffect(() => {
    if (!user || !token) {
      setQueue([]);
      setCurrent(null);
      setOpen(false);
      unauthorizedRef.current = false;
      stopPolling();
      return undefined;
    }
    // Reset del flag al re-loguearse (token/user nuevo)
    unauthorizedRef.current = false;
    fetchActive();
    intervalRef.current = setInterval(fetchActive, POLL_INTERVAL_MS);
    return () => {
      stopPolling();
    };
  }, [user, token, fetchActive, stopPolling]);

  // Pop next from queue if nothing currently shown
  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setOpen(true);
    }
  }, [queue, current]);

  const dismiss = async () => {
    if (!current) return;
    const id = current.id;
    try {
      await axios.post(
        `${API}/reportes-errores/anuncios/${id}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // even on error close locally — server will retry on next poll
    }
    setOpen(false);
    setTimeout(() => {
      setQueue((prev) => prev.filter((a) => a.id !== id));
      setCurrent(null);
    }, 220);
  };

  if (!user || !current) return null;

  const sevClass = SEVERIDAD_BADGE[current.severidad] || SEVERIDAD_BADGE.medio;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="bug-resolved-announcement-dialog"
      >
        {/* Hero banner */}
        <div className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 px-6 pt-7 pb-8 text-white">
          <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-white/15 blur-3xl" />
          <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start gap-3">
            <div className="bg-white/25 backdrop-blur-sm rounded-xl p-2.5 ring-1 ring-white/30">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <Badge className="bg-white/95 text-emerald-700 border-0 font-semibold tracking-wide text-[10px] uppercase mb-2">
                <Sparkles className="w-2.5 h-2.5 mr-1" /> Problema resuelto
              </Badge>
              <DialogHeader className="space-y-1">
                <DialogTitle className="text-white text-xl font-bold leading-tight pr-6">
                  ¡Resolvimos el problema que reportaste!
                </DialogTitle>
                <DialogDescription className="text-white/90 text-[13px] leading-relaxed">
                  Gracias por avisarnos. Tu reporte ya fue atendido y solucionado por el equipo.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 bg-white dark:bg-slate-900 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-0.5">
                Problema
              </p>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {current.asunto}
              </p>
            </div>
            <Badge variant="outline" className={`text-[10px] ${sevClass}`}>
              {current.severidad}
            </Badge>
          </div>

          {current.modulo && (
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <ArrowRight className="w-3 h-3" />
              <span>
                Módulo afectado: <strong className="text-slate-700 dark:text-slate-200">{current.modulo}</strong>
              </span>
            </div>
          )}

          {current.resolution_notes && (
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 p-3">
              <p className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold mb-1">
                Nota del equipo
              </p>
              <p className="text-[13px] text-emerald-900 dark:text-emerald-100 whitespace-pre-wrap leading-relaxed">
                {current.resolution_notes}
              </p>
            </div>
          )}

          {current.resolved_by && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              Resuelto por <strong>{current.resolved_by}</strong>
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              className="text-slate-500 hover:text-slate-700"
              data-testid="bug-resolved-close-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Cerrar
            </Button>
            <Button
              onClick={dismiss}
              className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-md shadow-emerald-200/50 dark:shadow-emerald-900/30"
              data-testid="bug-resolved-acknowledge-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Entendido, ¡gracias!
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
